import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import { Menu, X, Monitor } from "lucide-react";

const TITLES = {
  "/": "Dashboard",
  "/pos": "POS Terminal",
  "/products": "Products",
  "/customers": "Customers",
  "/parties": "Parties",
  "/purchase-bills": "Purchase Bills",
  "/expenses": "Expenses",
  "/bills": "Sales Bills",
  "/returns": "Sales Returns",
  "/users": "Users & Roles",
  "/branches": "Branches",
  "/pos-terminals": "POS Terminals",
  "/loyalty": "Loyalty Program",
  "/campaigns": "WhatsApp Campaigns",
  "/approvals": "Approval Queue",
  "/bulk-import": "Bulk Import",
  "/barcode-labels": "Barcode Labels",
  "/bi": "Business Intelligence",
  "/templates": "WhatsApp Templates",
  "/triggers": "Auto Triggers",
  "/reports": "Sales Reports",
  "/audit-logs": "Audit Logs",
  "/busy-export": "Busy Accounting Export",
  "/settings": "Organisation Settings",
  "/staff": "Staff · Attendance · Salary",
};

export default function Layout() {
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isPOS = pathname === "/pos";

  if (isPOS) {
    return (
      <div className="flex min-h-screen bg-slate-50 relative">
        {/* Sidebar: hidden under md (POS needs the whole screen on tablet) */}
        <div className="hidden md:block">
          <Sidebar />
        </div>
        {/* Mobile drawer for POS too */}
        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
        <main className="flex-1 min-w-0 overflow-hidden">
          {/* Phone notice: POS is tablet/desktop only */}
          <div className="md:hidden h-screen flex flex-col items-center justify-center px-6 text-center bg-slate-50" data-testid="pos-mobile-notice">
            <button
              onClick={() => setDrawerOpen(true)}
              className="absolute top-4 left-4 p-2 border border-slate-300 bg-white"
              data-testid="mobile-menu-btn-pos"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
            <Monitor className="w-14 h-14 text-slate-400 mb-4" strokeWidth={1.5} />
            <div className="text-lg font-semibold text-slate-900 mb-1">Open POS on a tablet or desktop</div>
            <div className="text-sm text-slate-500 max-w-xs leading-relaxed">
              The POS terminal is optimised for screens ≥ 768px wide. On smaller devices the cart, product grid and payment flow get cramped. Open the menu to use other features such as bills, products, BI and reports.
            </div>
          </div>
          {/* Tablet+ shows real POS */}
          <div className="hidden md:block h-full"><Outlet /></div>
        </main>
      </div>
    );
  }

  const key = Object.keys(TITLES).find((k) => pathname === k || (k !== "/" && pathname.startsWith(k)));
  const title = key ? TITLES[key] : "eaassy";

  return (
    <div className="flex min-h-screen bg-slate-50 relative">
      {/* Desktop sidebar */}
      <div className="hidden md:block">
        <Sidebar />
      </div>
      {/* Mobile drawer */}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 md:h-16 border-b border-slate-200 bg-white flex items-center justify-between px-3 md:px-6 flex-shrink-0 gap-2" data-testid="page-header">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 -ml-2 hover:bg-slate-100"
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <h1 className="text-base md:text-xl font-semibold tracking-tight text-slate-900 truncate" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>{title}</h1>
          </div>
          <div className="hidden sm:block text-[10px] md:text-xs uppercase tracking-[0.2em] text-slate-500 whitespace-nowrap">
            {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </header>
        <main className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overflow-auto"><Outlet /></main>
      </div>
    </div>
  );
}

function MobileDrawer({ open, onClose }) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        data-testid="mobile-drawer-backdrop"
      />
      {/* Drawer */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="mobile-drawer"
        onClick={onClose}
      >
        <div className="relative h-full">
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="absolute -right-10 top-3 p-2 bg-slate-900 text-white"
            aria-label="Close menu"
            data-testid="mobile-drawer-close"
          >
            <X size={18} />
          </button>
          <div onClick={(e) => e.stopPropagation()}>
            <Sidebar onNavigate={onClose} />
          </div>
        </div>
      </div>
    </>
  );
}
